import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, elements, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../styles/count_graphs.css'
import { BiFullscreen, BiExitFullscreen, BiLineChart, BiBarChartAlt2 } from 'react-icons/bi';


const DailyInfringementChart = ({ 
    dailyInfringementData,
    dailyDataLoading,
    selectedDate,
    selectedDayOfWeek,
    selectedWeekStartDate,
    selectedMonth,
    totalDailyInfringements,
    dailyInfringementPercentageIncrease 
}) => {

    const [barChartData, setBarChartData] = useState();
    const [lineChartData, setLineChartData] = useState();
    const [chartType, setChartType] = useState('line');
    const barChartRef = useRef(null);
    const lineChartRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const smallScreen = window.innerWidth < 786;

    const getMonthName = (monthNumber) => {
        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        return months[monthNumber - 1];
    };

    const prepareDailyInfringementBarChart = () => {
        return {
            labels: dailyInfringementData.map(d => d.day),
            datasets: [
                {
                    label: 'Daily Infringements',
                    data: dailyInfringementData.map(d => d.daily_infringements),
                    backgroundColor: '#FFC400',
                    borderWidth: 1
                }
            ]
            
        };
    };

    const getDailyInfringementBarChartOptions = () => {
        return {
            responsive: true, // Ensure the chart is responsive
            maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
            hover: {
                mode: 'index',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            elements: {
                points: {
                    radius:4,
                    hoverRadius: 6
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: (context) => {
                            const labelDay = dailyInfringementData[context.index]?.day;
                            return labelDay === selectedDayOfWeek ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelDay = dailyInfringementData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelDay = dailyInfringementData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 14 : 12;
                            }
                        }
                    },
                    title: {
                        display: true,
                        text: 'Days of the week',
                    },
                    stacked: true, 
                },
                y: {
                    min: 0, // Set minimum value for y-axis
                    title: {
                        display: true,
                        text: 'Infringement Count',
                    },
                    stacked: true, 
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', // Show tooltip for all datasets at the same index
                    intersect: 'false'
                },
                legend: {
                    position: 'top', // Position the legend at the top
                    labels: {
                        font: {
                            size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                        },
                        boxWidth: smallScreen ? 8 : 40,  // Adjust width of legend marker box
                        boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                },
            },

        }
    }

    const getDailyInfringementLineChartOptions = () => {
        return {
            responsive: true, // Ensure the chart is responsive
            maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
            hover: {
                mode: 'index',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            elements: {
                points: {
                    radius:4,
                    hoverRadius: 6
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: (context) => {
                            const labelDay = dailyInfringementData[context.index]?.day;
                            return labelDay === selectedDayOfWeek ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelDay = dailyInfringementData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelDay = dailyInfringementData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 14 : 12;
                            }
                        }
                    },
                    title: {
                        display: true,
                        text: 'Days of the week',
                    },
                },
                y: {
                    beginAtZero: true, // Ensure y-axis starts at zero
                    title: {
                        
                        display: true,
                        text: 'Infringement Count',
                    },
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', // Show tooltip for all datasets at the same index
                    intersect: 'false'
                },
                legend: {
                    position: 'top', // Position the legend at the top
                    labels: {
                        font: {
                            size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                        },
                        boxWidth: smallScreen ? 8 : 40,  // Adjust width of legend marker box
                        boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                },
            },

        }
    }

    const prepareDailyInfringementLineChart = () => {
        return {
            labels: dailyInfringementData.map(d => d.day),
            datasets: [
                {
                    label: 'Daily Infringements',
                    data: dailyInfringementData.map(d => d.daily_infringements),
                    backgroundColor: '#FFC400',
                    borderColor: '#FFC400',
                    borderWidth: 2,
                    tension: 0.3
                }
            ]
            
        };
    };

    const toggleChartType = () => {
        setChartType(prevType => prevType === 'bar' ? 'line' : 'bar');
    };

    useEffect(() => {
        if (dailyInfringementData.length) {
            const preparedLineChart = prepareDailyInfringementLineChart();
            if (preparedLineChart) {
                setLineChartData(preparedLineChart);
            }
            const preparedBarChart = prepareDailyInfringementBarChart();
            if (preparedBarChart) {
                setBarChartData(preparedBarChart);
            }
        }
    }, [dailyInfringementData]);


    Chart.register(...registerables );
    Chart.defaults.color = '#fff'; 
    return (
        <>
            <div className={`count-bar-chart-wrapper ${isFullscreen ? 'fullscreen-container' : ''}`}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', }}>
                    {/* Left-aligned title */}
                    <div className='graph-title' style={{ flex: '1 1 auto', minWidth: '200px' }}>
                        Daily Infringements
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        justifyContent: 'flex-end',
                        flex: '1 1 auto',
                        minWidth: '100px',
                    }}>      
                        <button className='toggle-chart-button' onClick={() => toggleChartType('month')}>
                            {chartType ? (
                            <BiBarChartAlt2 size={24} title='Switch to Bar Chart' color='white' />
                            ) : (
                            <BiLineChart size={24} title='Switch to Line Chart' color='white' />
                            )}
                        </button>

                        <button
                            className='toggle-chart-button'
                            onClick={() => setIsFullscreen(prev => !prev)}
                            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? (
                            <BiExitFullscreen size={24} color="white" />
                            ) : (
                            <BiFullscreen size={24} color="white" />
                            )}
                        </button>
                        <div className='count-chart-footer-percentage-container'>
                            <div className='lpr-chart-footer-percentage-icon'>
                                {
                                    dailyInfringementPercentageIncrease > 0 ? <svg style={{ color: '#32de84' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-up-right-circle" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z" />
                                    </svg> :
                                        <svg style={{ color: '#EF5B5B' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-down-right-circle" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z" />
                                        </svg>
                                }

                            </div>
                            <div className='lpr-chart-footer-percentage-value' style={{ color: dailyInfringementPercentageIncrease > 0 ? '#32de84' : '#EF5B5B' }}>{dailyInfringementPercentageIncrease}%</div>
                        </div>
                    </div>
                </div>  

                <div className={dailyDataLoading ? 'lpr-weekly-bar-chart-container-loading' : ''}>
                    {
                        chartType === 'line' ?
                            <Line
                                ref={lineChartRef}
                                data={lineChartData || { labels: [], datasets: [] }} // Fetch prepared data
                                options={getDailyInfringementLineChartOptions()}
                            />
                            :
                            <Bar 
                                ref={barChartRef}
                                data={barChartData || { labels: [], datasets: [] }} // Fetch prepared data
                                options={getDailyInfringementBarChartOptions()}
                            />

                    }

                    {
                        dailyDataLoading && <div id="lpr-daily-loading-overlay">
                            <BeatLoader color={'white'} loading={dailyDataLoading} size={15} />
                        </div>
                    }
                </div>

            </div>

        </>
    )
}


export default DailyInfringementChart;